import React, { useRef, forwardRef, useEffect, useState } from "react";

/**
 * Component CustomTextArea giải quyết vấn đề mất focus khi click vào textarea
 *
 * @param {string} value - Giá trị hiện tại của textarea (controlled component)
 * @param {function} onChange - Hàm xử lý khi giá trị thay đổi
 * @param {string} defaultValue - Giá trị mặc định của textarea (uncontrolled component)
 * @param {function} onBlur - Hàm xử lý khi blur khỏi textarea
 * @param {number} rows - Số dòng của textarea
 * @param {string} placeholder - Placeholder của textarea
 * @param {string} className - Class CSS bổ sung
 * @param {function} onKeyDown - Hàm xử lý khi nhấn phím
 * @returns {JSX.Element}
 */
const CustomTextArea = forwardRef(
  (
    {
      value,
      onChange,
      defaultValue = "",
      onBlur,
      rows = 4,
      placeholder = "",
      className = "",
      onKeyDown,
    },
    ref
  ) => {
    const internalRef = useRef(null);
    const textareaRef = ref || internalRef;
    const [internalValue, setInternalValue] = useState(
      value || defaultValue || ""
    );

    // Cập nhật internalValue khi prop value thay đổi (để component hoạt động như controlled component)
    useEffect(() => {
      if (value !== undefined) {
        setInternalValue(value);
      }
    }, [value]);

    // Xử lý khi giá trị thay đổi
    const handleChange = (e) => {
      const newValue = e.target.value;
      setInternalValue(newValue);

      if (onChange) {
        onChange(e);
      }
    };

    // Sử dụng cách tiếp cận uncontrolled component để tránh mất focus
    const handleBlur = (e) => {
      if (onBlur) {
        onBlur(e);
      }
    };

    // Xử lý sự kiện focus để giữ focus khi click vào textarea
    const handleFocus = () => {
      // Không cần làm gì đặc biệt, chỉ cần đảm bảo sự kiện này được gắn vào textarea
    };

    // Khi component mount, điều chỉnh chiều cao ban đầu
    useEffect(() => {
      // Sử dụng requestAnimationFrame để đảm bảo DOM đã được render hoàn toàn
      // và việc điều chỉnh chiều cao được thực hiện mượt mà
      const timeoutId = setTimeout(() => {
        window.requestAnimationFrame(adjustHeight);
      }, 10);

      return () => clearTimeout(timeoutId);
    }, []);

    // Tự động điều chỉnh chiều cao của textarea
    const adjustHeight = () => {
      if (textareaRef.current) {
        // Lưu lại vị trí con trỏ và selection
        const selectionStart = textareaRef.current.selectionStart;
        const selectionEnd = textareaRef.current.selectionEnd;

        // Điều chỉnh chiều cao
        textareaRef.current.style.height = "auto";
        const scrollHeight = textareaRef.current.scrollHeight;
        const minHeight = rows * 24;
        const maxHeight = Math.min(scrollHeight, 400); // Giới hạn chiều cao tối đa 400px
        const newHeight = Math.max(scrollHeight, minHeight);

        // Nếu nội dung vượt quá 400px, cho phép scroll
        if (scrollHeight > 400) {
          textareaRef.current.style.height = "400px";
          textareaRef.current.style.overflowY = "auto";
        } else {
          textareaRef.current.style.height = `${newHeight}px`;
          textareaRef.current.style.overflowY = "hidden";
        }

        // Khôi phục vị trí con trỏ và selection
        textareaRef.current.setSelectionRange(selectionStart, selectionEnd);
      }
    };

    // Sử dụng requestAnimationFrame để tránh flicker
    const handleInput = () => {
      // Sử dụng requestAnimationFrame để đảm bảo việc điều chỉnh chiều cao được thực hiện ở frame tiếp theo
      window.requestAnimationFrame(adjustHeight);
    };

    const handleKeyDown = (e) => {
      // Xử lý các phím tắt như Ctrl+Enter hoặc Escape
      if (onKeyDown) {
        onKeyDown(e);
      }

      // Tự động điều chỉnh chiều cao khi nhấn Enter
      if (e.key === "Enter") {
        window.requestAnimationFrame(adjustHeight);
      }
    };

    // Xử lý sự kiện click trực tiếp trên textarea
    const handleClick = () => {
      // Đảm bảo textarea giữ được focus khi click
      if (
        textareaRef.current &&
        document.activeElement !== textareaRef.current
      ) {
        textareaRef.current.focus();
      }
    };

    // Quyết định sử dụng controlled hoặc uncontrolled component
    const textareaProps =
      value !== undefined
        ? { value: internalValue } // Controlled mode
        : { defaultValue }; // Uncontrolled mode

    return (
      <textarea
        ref={textareaRef}
        className={`w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-gray-700 resize-none ${className}`}
        rows={rows}
        {...textareaProps}
        onChange={handleChange}
        onInput={handleInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        style={{
          minHeight: `${rows * 24}px`,
          maxHeight: "400px",
          transition: "height 0.1s ease",
          overflowY: "hidden",
        }}
      />
    );
  }
);

export default CustomTextArea;
